// One order for every menu (Features 4, Tidy; DESIGN.md, "The UI guide"): a widget's ⋯ and its right-click, the
// board menu, a board's right-click, the canvas's, and the phone's sheets. The thing's own first (open it, its
// settings), then asking the agent, then its data, then copying and viewing, then what is rarely used, folded under
// More once the menu would run past seven rows, then a line and Delete, last and in red.

import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cx } from "../../lib/util";

export type MenuGroup = "main" | "ask" | "data" | "edit" | "more" | "delete";

export interface MenuEntry {
  label: string;
  group: MenuGroup;
  icon?: ReactNode;
  kbd?: string;
  /** a quiet word at the row's end, where a key would be ("On", "1 to review") */
  note?: string;
  run: () => void;
  danger?: boolean;
  disabled?: boolean;
}

export type MenuRow = MenuEntry | "sep" | "more";

/** The groups in their one order; a line falls before data, before copying and viewing, and before Delete. */
export const MENU_ORDER: MenuGroup[] = ["main", "ask", "data", "edit", "more", "delete"];
const LINE_BEFORE: MenuGroup[] = ["data", "edit", "delete"];
/** A menu longer than this folds its rarely used rows under More. */
export const MORE_AFTER = 7;

/** Whether a menu of these rows folds its rarely used ones under More. */
export const folds = (entries: MenuEntry[]) => entries.length > MORE_AFTER && entries.filter((e) => e.group === "more").length >= 2;

/** The rows as a menu shows them: in the one order, with its lines, and More open or shut. */
export function arrange(entries: MenuEntry[], moreOpen: boolean): MenuRow[] {
  const fold = folds(entries);
  const rows: MenuRow[] = [];
  for (const g of MENU_ORDER) {
    const list = entries.filter((e) => e.group === g);
    if (!list.length) continue;
    if (rows.length && LINE_BEFORE.includes(g) && rows.at(-1) !== "sep") rows.push("sep");
    // what is rarely used sits with copying and viewing, under a line of its own when that section is empty
    if (g === "more" && rows.length && !entries.some((e) => e.group === "edit") && rows.at(-1) !== "sep") rows.push("sep");
    if (g === "more" && fold) {
      rows.push("more");
      if (moreOpen) rows.push(...list);
    } else rows.push(...list);
  }
  return rows;
}

/**
 * A menu's rows, its keys (up and down, Enter, right to open More, left to shut it, Esc) and its More. Each row runs
 * after the menu closes, so what it opens is never under the menu.
 */
export function MenuList({ entries, onClose }: { entries: MenuEntry[]; onClose: () => void }) {
  const [more, setMore] = useState(false);
  const [active, setActive] = useState(-1);
  const rows = arrange(entries, more);
  const usable = rows.map((r, i) => ({ r, i })).filter((o) => o.r !== "sep" && !(o.r !== "more" && o.r.disabled));
  const pick = (r: MenuRow) => {
    if (r === "sep") return;
    if (r === "more") return setMore((m) => !m);
    onClose();
    r.run();
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const cur = usable.findIndex((o) => o.i === active);
        const next = usable[(cur + (e.key === "ArrowDown" ? 1 : -1) + usable.length) % usable.length];
        if (next) setActive(next.i);
      } else if (e.key === "ArrowRight" && rows[active] === "more") setMore(true);
      else if (e.key === "ArrowLeft" && more) {
        setMore(false);
        setActive(rows.indexOf("more"));
      } else if (e.key === "Enter" && active >= 0) {
        e.preventDefault();
        pick(rows[active]);
      }
      // every key is the open menu's: Delete or a letter never reaches the board's shortcuts behind it (QA, 26 Oct)
      e.stopPropagation();
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  });
  const inMore = (r: MenuEntry) => more && r.group === "more" && folds(entries);
  return (
    <>
      {rows.map((r, i) =>
        r === "sep" ? (
          <div key={"sep" + i} className="menu-sep" />
        ) : r === "more" ? (
          <button key="more" role="menuitem" aria-expanded={more} className={cx("menu-item menu-more", more && "is-open", active === i && "is-active")} onMouseEnter={() => setActive(i)} onClick={() => setMore(!more)}>
            {/* as wide as the rows' icons, so More lines up with them (Fixes 10) */}
            <span className="menu-ico" style={{ width: 16 }} />
            <span className="menu-label">More</span>
            <ChevronDown size={13} className="menu-more-chev" />
          </button>
        ) : (
          <button
            key={r.label}
            role="menuitem"
            className={cx("menu-item", r.danger && "is-danger", active === i && "is-active", inMore(r) && "is-under")}
            disabled={r.disabled}
            onMouseEnter={() => setActive(i)}
            onClick={() => pick(r)}
          >
            <span className="menu-ico" style={{ width: 16 }}>
              {r.icon}
            </span>
            <span className="menu-label">{r.label}</span>
            {r.note ? <span className="menu-kbd">{r.note}</span> : r.kbd ? <span className="menu-kbd">{r.kbd}</span> : null}
          </button>
        ),
      )}
    </>
  );
}
